import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Typography,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Space,
  InputNumber,
  message,
  Tag,
  Modal,
  Radio,
  Divider,
  Spin
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  SaveOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { invoiceService, customerService, productService, paymentService, shippingService } from '../../services';
import { formatCurrency } from '../../utils/helpers';

const { Title } = Typography;
const { Option } = Select;

const InvoiceExportForm = () => {
  const [form] = Form.useForm();
  
  // Watch address fields for auto-calculation
  const watchedProvince = Form.useWatch('province', form);
  const watchedDistrict = Form.useWatch('district', form);
  const watchedWard = Form.useWatch('ward', form);
  
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [invoiceStatuses, setInvoiceStatuses] = useState([]);
  const navigate = useNavigate();

  // Modal states
  const [variantModalVisible, setVariantModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [inputQuantity, setInputQuantity] = useState(1);

  // States for calculations
  const [subtotal, setSubtotal] = useState(0);
  const [shippingFee, setShippingFee] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [total, setTotal] = useState(0);

  // States for customer addresses
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [addressType, setAddressType] = useState(null);

  // States for QR payment
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [currentInvoice, setCurrentInvoice] = useState(null); // 'existing', 'pickup', 'new'

  // States for shipping calculation
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [selectedShippingOption, setSelectedShippingOption] = useState(null);
  
  // State for payment method
  const [paymentMethod, setPaymentMethod] = useState('Tiền mặt');

  // States for location data
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState([]);
  const [locationLoading, setLocationLoading] = useState({
    provinces: false,
    districts: false,
    wards: false
  });

  useEffect(() => {
    // Không tải khách hàng ngay từ đầu - chỉ tải khi search
    loadProducts();
    loadInvoiceStatuses();
    loadProvinces(); // Load danh sách tỉnh khi component mount
    
    // Set giá trị mặc định cho form
    form.setFieldsValue({
      paymentMethod: 'Tiền mặt'
    });

    // Mobile detection
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [selectedItems, shippingFee, discountAmount]);

  // Auto calculate shipping when relevant data changes
  useEffect(() => {
    const autoCalculateShipping = async () => {
      // Reset shipping for pickup
      if (addressType === 'pickup') {
        setShippingOptions([]);
        setSelectedShippingOption(null);
        setShippingFee(0);
        //console.log('🏪 Pickup selected - shipping fee reset to 0');
        return;
      }

      // Only auto-calculate if has products in cart
      if (selectedItems.length > 0 && addressType && addressType !== 'pickup') {
        const formValues = form.getFieldsValue();
        
        // For new address: need province, district, ward
        if (addressType === 'new' && formValues.province && formValues.district && formValues.ward) {
          //console.log('🔄 Auto-calculating shipping for new address...');
          await handleCalculateShipping(true);
        }
        // For existing address: auto-calculate if address is available
        else if (addressType !== 'new' && customerAddresses.length > 0) {
          const selectedAddress = customerAddresses.find(addr => addr.id.toString() === addressType);
          if (selectedAddress) {
            //console.log('🔄 Auto-calculating shipping for existing address...');
            await handleCalculateShipping(true);
          }
        }
      }
    };

    // Debounce auto-calculation to avoid too many API calls
    const timer = setTimeout(autoCalculateShipping, 1000);
    return () => clearTimeout(timer);
  }, [selectedItems, addressType, customerAddresses]); // Dependencies that affect shipping

  // Auto-calculate shipping when address fields change (for new address)
  useEffect(() => {
    if (addressType === 'new' && selectedItems.length > 0) {
      const autoCalcForAddressChange = async () => {
        //console.log('📋 Watched address values:', {
        //   province: watchedProvince,
        //   district: watchedDistrict,
        //   ward: watchedWard,
        //   addressType,
        //   selectedItemsCount: selectedItems.length
        // });
        
        if (watchedProvince && watchedDistrict && watchedWard) {
          //console.log('🔄 Auto-calculating shipping due to address change...');
          await handleCalculateShipping(true);
        } else {
          // //console.log('⚠️ Not all address fields filled yet:', {
          //   province: !!watchedProvince,
          //   district: !!watchedDistrict,
          //   ward: !!watchedWard
          // });
        }
      };

      // Debounce for address field changes
      const timer = setTimeout(autoCalcForAddressChange, 1500);
      return () => clearTimeout(timer);
    } else {
      //console.log('ℹ️ Auto-calc conditions not met:', {
      //   addressType,
      //   isNew: addressType === 'new',
      //   hasItems: selectedItems.length > 0,
      //   itemCount: selectedItems.length
      // });
    }
  }, [watchedProvince, watchedDistrict, watchedWard, addressType, selectedItems]);

  // Auto-calculate shipping when address is completed (all fields filled at once)
  const prevAddressCompleteRef = useRef(false);
  useEffect(() => {
    //console.log('🔄 Address completion useEffect triggered');
    
    if (addressType === 'new' && selectedItems.length > 0) {
      const isAddressComplete = watchedProvince && watchedDistrict && watchedWard;

      //console.log('🔍 Address completion check:', {
      //   addressType,
      //   selectedItemsCount: selectedItems.length,
      //   province: watchedProvince,
      //   district: watchedDistrict,
      //   ward: watchedWard,
      //   isAddressComplete,
      //   prevComplete: prevAddressCompleteRef.current
      // });

      // Only trigger if address just became complete
      if (isAddressComplete && !prevAddressCompleteRef.current) {
        //console.log('✅ Address just became complete! Auto-calculating shipping...');
        // Small delay to ensure form is fully updated
        const timer = setTimeout(() => {
          handleCalculateShipping(true);
        }, 500);
        
        prevAddressCompleteRef.current = isAddressComplete;
        return () => clearTimeout(timer);
      }

      prevAddressCompleteRef.current = isAddressComplete;
    } else {
      //console.log('❌ Completion check conditions not met:', {
      //   addressType,
      //   isNew: addressType === 'new',
      //   hasItems: selectedItems.length > 0,
      //   itemCount: selectedItems.length
      // });
      prevAddressCompleteRef.current = false;
    }
  }, [watchedProvince, watchedDistrict, watchedWard, addressType, selectedItems]);

  const loadInvoiceStatuses = async () => {
    try {
      //console.log('📋 Loading invoice statuses...');
      const response = await invoiceService.export.getStatuses();
      
      if (response.success) {
        setInvoiceStatuses(response.data);
        //console.log('✅ Invoice statuses loaded:', response.data);
      } else {
        //console.error('❌ Failed to load invoice statuses:', response.message);
        message.error('Không thể tải danh sách trạng thái hóa đơn');
      }
    } catch (error) {
      //console.error('💥 Error loading invoice statuses:', error);
      message.error('Lỗi khi tải danh sách trạng thái hóa đơn');
    }
  };

  // Load provinces from ViettelPost API
  const loadProvinces = async () => {
    try {
      setLocationLoading(prev => ({ ...prev, provinces: true }));
      //console.log('🏙️ Loading provinces from ViettelPost...');
      
      const response = await shippingService.getProvinces();
      
      if (response.success && response.data) {
        setProvinces(response.data);
        //console.log('✅ Provinces loaded:', response.data.length);
      } else {
        //console.error('❌ Failed to load provinces');
        message.error('Không thể tải danh sách tỉnh/thành phố');
      }
    } catch (error) {
      //console.error('💥 Error loading provinces:', error);
      message.error('Lỗi khi tải danh sách tỉnh/thành phố');
    } finally {
      setLocationLoading(prev => ({ ...prev, provinces: false }));
    }
  };

  // Load districts by province
  const loadDistricts = async (provinceId) => {
    try {
      setLocationLoading(prev => ({ ...prev, districts: true }));
      setDistricts([]);
      setWards([]);
      
      if (!provinceId) return;
      
      //console.log('🏘️ Loading districts for province:', provinceId);
      
      const response = await shippingService.getDistricts(provinceId);
      
      if (response.success && response.data) {
        setDistricts(response.data);
        //console.log('✅ Districts loaded:', response.data.length);
      } else {
        //console.error('❌ Failed to load districts');
        message.error('Không thể tải danh sách quận/huyện');
      }
    } catch (error) {
      //console.error('💥 Error loading districts:', error);
      message.error('Lỗi khi tải danh sách quận/huyện');
    } finally {
      setLocationLoading(prev => ({ ...prev, districts: false }));
    }
  };

  // Load wards by district
  const loadWards = async (districtId) => {
    try {
      setLocationLoading(prev => ({ ...prev, wards: true }));
      setWards([]);
      
      if (!districtId) return;
      
      //console.log('🏠 Loading wards for district:', districtId);
      
      const response = await shippingService.getWards(districtId);
      
      if (response.success && response.data) {
        setWards(response.data);
        //console.log('✅ Wards loaded:', response.data.length);
      } else {
        //console.error('❌ Failed to load wards');
        message.error('Không thể tải danh sách phường/xã');
      }
    } catch (error) {
      //console.error('💥 Error loading wards:', error);
      message.error('Lỗi khi tải danh sách phường/xã');
    } finally {
      setLocationLoading(prev => ({ ...prev, wards: false }));
    }
  };

  const loadCustomers = async (keyword = '') => {
    try {
      // Debug: Log thông tin
      //console.log('🔍 Loading customers with keyword:', keyword);
      
      // Chỉ tìm khi có keyword (ít nhất 2 ký tự)
      if (keyword.length < 2) {
        //console.log('⚠️ Keyword too short, clearing customers');
        setCustomers([]); // Xóa danh sách cũ
        return;
      }
      
      const accessToken = localStorage.getItem('accessToken');
      //console.log('🔑 AccessToken:', accessToken ? accessToken.substring(0, 20) + '...' : 'NOT FOUND');
      
      const params = { 
        keyword: keyword,
        limit: 10, 
        offset: 0 
      };
      //console.log('📤 Request params:', params);
      //console.log('🔍 DEBUG: About to call customerService.getAll with token:', localStorage.getItem('accessToken')?.substring(0, 20) + '...');
      
      const response = await customerService.getAll(params);
      //console.log('📥 Customer response:', response);
      
      if (response.success) {
        //console.log('✅ Customers loaded:', response.data?.length || 0);
        setCustomers(response.data || []);
      } else {
        //console.error('❌ API returned success=false:', response.message);
        setCustomers([]);
        message.error(response.message || 'Không thể tải danh sách khách hàng');
      }
    } catch (error) {
      //console.error('💥 Error loading customers:', error);
      //console.error('💥 Error details:', {
      //   message: error.message,
      //   status: error.response?.status,
      //   data: error.response?.data
      // });
      setCustomers([]);
      message.error('Không thể tải danh sách khách hàng: ' + (error.response?.data?.message || error.message));
    }
  };

  const loadCustomerAddresses = async (customerId) => {
    try {
      //console.log('🏠 Loading addresses for customer:', customerId);
      
      const response = await customerService.getAddresses(customerId);
      //console.log('🏠 Customer addresses response:', response);
      
      if (response.success && response.data) {
        // Chuyển đổi dữ liệu từ API thành format phù hợp cho UI
        const addresses = response.data.map(addr => ({
          id: addr.id,
          name: addr.address_name || `${addr.ward}, ${addr.district}`,
          address: [
            addr.house_number,
            addr.street,
            addr.address_detail,
            addr.ward,
            addr.district,
            addr.province
          ].filter(Boolean).join(', '),
          phone: addr.phone,
          receiver_name: addr.receiver_name,
          is_default: addr.is_default === 1,
          note: addr.note
        }));
        
        //console.log('🏠 Processed addresses:', addresses);
        setCustomerAddresses(addresses);
      } else {
        //console.log('🏠 No addresses found or API error:', response);
        setCustomerAddresses([]);
      }
    } catch (error) {
      //console.error('💥 Error loading customer addresses:', error);
      setCustomerAddresses([]);
      message.error('Không thể tải danh sách địa chỉ: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCustomerChange = (customerId) => {
    //console.log('👤 Customer selected:', customerId);
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    
    if (customerId) {
      loadCustomerAddresses(customerId);
      // Reset address selection
      setAddressType(null);
      form.setFieldsValue({
        addressType: null,
        receiverName: customer?.full_name || '',
        phone: customer?.SDT || ''
      });
    } else {
      setCustomerAddresses([]);
      setAddressType(null);
      form.setFieldsValue({
        addressType: null,
        receiverName: '',
        phone: '',
        address: ''
      });
    }
  };

  const handleAddressTypeChange = (type) => {
    setAddressType(type);
    
    // Reset shipping data when changing address type
    setShippingOptions([]);
    setSelectedShippingOption(null);
    setShippingFee(0);
    
    // Reset location data
    setDistricts([]);
    setWards([]);
    
    if (type === 'pickup') {
      // Nhận tại quầy
      form.setFieldsValue({
        receiverName: selectedCustomer?.full_name || '',
        phone: selectedCustomer?.SDT || '',
        address: 'Nhận tại quầy',
        // Clear địa chỉ chi tiết
        province: null,
        district: null,
        ward: null,
        street: '',
        houseNumber: '',
        addressDetail: '',
        note: ''
      });
    } else if (type === 'new') {
      // Thêm địa chỉ mới - chỉ điền thông tin cơ bản, để trống địa chỉ
      form.setFieldsValue({
        receiverName: selectedCustomer?.full_name || '',
        phone: selectedCustomer?.SDT || '',
        address: '',
        // Clear tất cả fields địa chỉ chi tiết
        province: null,
        district: null,
        ward: null,
        street: '',
        houseNumber: '',
        addressDetail: '',
        note: ''
      });
    } else if (type && customerAddresses.length > 0) {
      // Chọn địa chỉ có sẵn
      const address = customerAddresses.find(addr => addr.id.toString() === type);
      if (address) {
        form.setFieldsValue({
          receiverName: address.receiver_name || selectedCustomer?.full_name || '',
          phone: address.phone || selectedCustomer?.SDT || '',
          address: address.address,
          // Clear fields địa chỉ chi tiết vì đã có địa chỉ sẵn
          province: null,
          district: null,
          ward: null,
          street: '',
          houseNumber: '',
          addressDetail: '',
          note: address.note || ''
        });
      }
    }
  };

  // Handle shipping fee calculation
  const handleCalculateShipping = async (isAutoCalc = false) => {
    try {
      //console.log('🚢 handleCalculateShipping called:', { isAutoCalc, addressType, selectedItemsCount: selectedItems.length });
      
      let shippingAddress = {};
      
      if (addressType === 'new') {
        // Địa chỉ mới - validate form fields (ít nhất một field phải có)
        const formValues = form.getFieldsValue();
        //console.log('📋 New address form values:', formValues);
        
        if (!formValues.province && !formValues.district && !formValues.ward) {
          //console.log('❌ No address fields filled');
          if (!isAutoCalc) message.warning('Vui lòng điền ít nhất một thông tin địa chỉ (Tỉnh/Quận/Phường)');
          return;
        }
        
        // Validate các field đã điền
        const fieldsToValidate = [];
        if (formValues.province) fieldsToValidate.push('province');
        if (formValues.district) fieldsToValidate.push('district');
        if (formValues.ward) fieldsToValidate.push('ward');
        
        //console.log('🔍 Fields to validate:', fieldsToValidate);
        
        if (fieldsToValidate.length > 0) {
          try {
            const values = await form.validateFields(fieldsToValidate);
            //console.log('✅ Form validation passed:', values);
            
            // Tìm tên từ ID
            const province = provinces.find(p => p.PROVINCE_ID === values.province);
            const district = districts.find(d => d.DISTRICT_ID === values.district);
            const ward = wards.find(w => w.WARDS_ID === values.ward);
            
            //console.log('🏙️ Found location data:', { province: province?.PROVINCE_NAME, district: district?.DISTRICT_NAME, ward: ward?.WARDS_NAME });
            
            if (!province || !district || !ward) {
              //console.log('❌ Missing location data:', { province: !!province, district: !!district, ward: !!ward });
              if (!isAutoCalc) message.error('Không tìm thấy thông tin địa chỉ. Vui lòng chọn lại.');
              return;
            }
            
            shippingAddress = {
              province: province.PROVINCE_NAME,
              district: district.DISTRICT_NAME,
              ward: ward.WARDS_NAME
            };
            
            //console.log('📍 Shipping address from form:', shippingAddress);
          } catch (error) {
            // Validation failed - missing required fields
            if (!isAutoCalc) message.warning('Vui lòng điền đầy đủ thông tin địa chỉ đã chọn');
            return;
          }
        } else {
          if (!isAutoCalc) message.warning('Vui lòng điền ít nhất một thông tin địa chỉ');
          return;
        }
      } else if (addressType && customerAddresses.length > 0) {
        // Địa chỉ có sẵn - parse từ địa chỉ đã chọn
        const selectedAddress = customerAddresses.find(addr => addr.id.toString() === addressType);
        if (!selectedAddress) {
          if (!isAutoCalc) message.error('Không tìm thấy địa chỉ đã chọn');
          return;
        }
        
        // Parse địa chỉ để lấy thông tin Tỉnh/Quận/Phường
        // Format: "123 Đường ABC, Phường XYZ, Quận DEF, Thành phố GHI"
        const addressParts = selectedAddress.address.split(', ');
        if (addressParts.length < 3) {
          if (!isAutoCalc) message.warning('Địa chỉ không đủ thông tin để tính phí ship. Vui lòng chọn "Thêm địa chỉ mới" và nhập đầy đủ thông tin.');
          return;
        }
        
        // Lấy 3 phần cuối: Phường, Quận, Tỉnh
        const ward = addressParts[addressParts.length - 3]?.replace(/^(Phường|Xã|Thị trấn)\s*/i, '').trim();
        const district = addressParts[addressParts.length - 2]?.replace(/^(Quận|Huyện|Thành phố|Thị xã)\s*/i, '').trim();
        const province = addressParts[addressParts.length - 1]?.replace(/^(Tỉnh|Thành phố)\s*/i, '').trim();
        
        if (!ward || !district || !province) {
          if (!isAutoCalc) message.warning('Không thể phân tích địa chỉ để tính phí ship. Vui lòng chọn "Thêm địa chỉ mới" để nhập chi tiết.');
          return;
        }
        
        shippingAddress = { province, district, ward };
        //console.log('📍 Parsed address:', shippingAddress);
      } else {
        if (!isAutoCalc) message.warning('Vui lòng chọn địa chỉ giao hàng');
        return;
      }
      
      if (selectedItems.length === 0) {
        if (!isAutoCalc) message.warning('Vui lòng thêm sản phẩm trước khi tính phí ship');
        return;
      }

      if (addressType === 'pickup') {
        if (!isAutoCalc) message.info('Đơn nhận tại quầy không có phí ship');
        return;
      }

      setShippingLoading(true);
      
      // Calculate total weight and value
      const totalWeight = selectedItems.reduce((sum, item) => {
        // Assume each product weighs 300g if not specified
        const weight = item.weight || 300;
        return sum + (weight * item.quantity);
      }, 0);
      
      const shippingData = {
        PhuongXa: shippingAddress.ward,
        QuanHuyen: shippingAddress.district, 
        TinhThanhPho: shippingAddress.province,
        PRODUCT_WEIGHT: totalWeight,
        PRODUCT_PRICE: subtotal,
        MONEY_COLLECTION: subtotal // COD amount
      };

      //console.log('📦 Calculating shipping for:', shippingData);
      
      // Sử dụng getPrices để lấy tất cả dịch vụ thay vì getSimplePrices
      const response = await shippingService.getPrices(shippingData);
      
      if (response.success && response.data && response.data.services) {
        const options = response.data.services.map(service => ({
          id: service.service_code,
          name: service.service_name,
          price: service.price,
          delivery_time: service.delivery_time,
          service_code: service.service_code,
          exchange_weight: service.exchange_weight || 0,
          extra_services: service.extra_services || []
        }));
        
        // Sắp xếp theo giá từ thấp đến cao
        options.sort((a, b) => a.price - b.price);
        
        if (options.length > 0) {
          setShippingOptions(options);
          if (!isAutoCalc) {
            message.success(`Tìm thấy ${options.length} dịch vụ vận chuyển`);
          }
          
          // Auto-select cheapest option for auto-calculation
          if (isAutoCalc && options.length > 0) {
            const cheapest = options[0]; // Already sorted by price
            setSelectedShippingOption(cheapest);
            setShippingFee(cheapest.price);
            //console.log(`🔄 Auto-selected cheapest: ${cheapest.name} - ${formatCurrency(cheapest.price)}`);
          }
          
          // Hiển thị thông tin tóm tắt
          if (response.data.summary) {
            //console.log('💰 Cheapest:', response.data.summary.cheapest_service);
            //console.log('⚡ Fastest:', response.data.summary.fastest_service);
          }
        } else {
          if (!isAutoCalc) message.warning('Không tìm thấy dịch vụ vận chuyển phù hợp');
        }
      } else {
        if (!isAutoCalc) message.error('Không thể tính phí ship: ' + (response.message || 'Lỗi không xác định'));
      }
    } catch (error) {
      //console.error('💥 Shipping calculation error:', error);
      if (error.response?.status === 404) {
        if (!isAutoCalc) message.info('API tính phí ship chưa khả dụng. Vui lòng nhập phí ship thủ công.');
      } else {
        if (!isAutoCalc) message.error('Lỗi tính phí ship: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setShippingLoading(false);
    }
  };

  // Handle shipping option selection
  const handleShippingOptionSelect = (option) => {
    setSelectedShippingOption(option);
    setShippingFee(option.price);
    message.success(`Đã chọn: ${option.name} - ${formatCurrency(option.price)}`);
  };

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
     //console.log('🔄 Loading products...');
      
      // Thử nhiều API theo thứ tự ưu tiên
      let response;
      let apiUsed = 'unknown';
      
      // 1. Thử API search advanced trước
      try {
        response = await productService.searchAdvanced({ 
          page: 1,
          pageSize: 500,
          sortBy: 'name',
          sortDir: 'asc'
        });
        apiUsed = 'searchAdvanced';
       //console.log('📦 Search advanced response:', response);
      } catch (searchError) {
       //console.log('⚠️ Search advanced failed:', searchError.message);
        
        // 2. Fallback to getAll API
        try {
          response = await productService.getAll({
            page: 1,
            pageSize: 500
          });
          apiUsed = 'getAll';
         //console.log('📦 GetAll response:', response);
        } catch (getAllError) {
         //console.log('⚠️ GetAll failed:', getAllError.message);
          
          // 3. Fallback to getFull API  
          try {
            response = await productService.getFull({
              page: 1,
              pageSize: 500
            });
            apiUsed = 'getFull';
           //console.log('📦 GetFull response:', response);
          } catch (getFullError) {
           //console.log('⚠️ GetFull failed:', getFullError.message);
            throw new Error('Tất cả API sản phẩm đều không khả dụng');
          }
        }
      }
      
      if (response && response.success) {
        const products = response.data || [];
        setProducts(products);
       //console.log(`✅ Products loaded via ${apiUsed}:`, products.length);
        if (products.length === 0) {
          message.info('Chưa có sản phẩm nào trong hệ thống');
        } else {
          message.success(`Đã tải ${products.length} sản phẩm (${apiUsed})`);
        }
      } else {
       //console.error('❌ Products API error:', response?.message);
        message.error(response?.message || `API ${apiUsed} trả về lỗi: Không thể tải danh sách sản phẩm`);
        setProducts([]);
      }
    } catch (error) {
     //console.error('💥 Error loading products:', error);
     //console.error('💥 Error details:', {
      //   message: error.message,
      //   status: error.response?.status,
      //   statusText: error.response?.statusText,
      //   data: error.response?.data
      // });
      
      // Hiển thị lỗi chi tiết hơn
      let errorMessage = 'Không thể tải danh sách sản phẩm';
      if (error.response?.status === 401) {
        errorMessage = 'Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Không có quyền truy cập API sản phẩm.';
      } else if (error.response?.status === 404) {
        errorMessage = 'API sản phẩm không tồn tại hoặc chưa được cấu hình.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Lỗi server. Vui lòng thử lại sau.';
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Lỗi kết nối mạng. Kiểm tra kết nối internet.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      message.error(errorMessage);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const calculateTotals = () => {
    const newSubtotal = selectedItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const newTotal = newSubtotal + shippingFee - discountAmount;
    
    setSubtotal(newSubtotal);
    setTotal(Math.max(0, newTotal));
  };

  // Helper function để an toàn render variant attributes
  const renderVariantAttributes = (variant) => {
    try {
      // Debug logging
     //console.log('🧬 Rendering attributes for variant:', {
      //   sku: variant.sku,
      //   hasAttributes: !!variant.attributes,
      //   hasVariantAttributes: !!variant.variant_attributes,
      //   hasProperties: !!variant.properties,
      //   hasOptions: !!variant.options,
      //   attributesType: typeof variant.attributes,
      //   attributesValue: variant.attributes
      // });
      
      // Helper function để safely render value
      const renderValue = (value) => {
        if (typeof value === 'string' || typeof value === 'number') {
          return String(value);
        } else if (typeof value === 'object' && value !== null) {
          // Xử lý object có cấu trúc {attribute_name, attribute_value}
          if (value.attribute_name && value.attribute_value) {
            return `${value.attribute_name}: ${value.attribute_value}`;
          }
          // Xử lý object khác
          if (value.name && value.value) {
            return `${value.name}: ${value.value}`;
          }
          // Fallback: JSON.stringify nhưng chỉ lấy các field quan trọng
          const importantKeys = ['name', 'value', 'attribute_name', 'attribute_value', 'title', 'label'];
          const filtered = {};
          importantKeys.forEach(key => {
            if (value[key] !== undefined) {
              filtered[key] = value[key];
            }
          });
          
          if (Object.keys(filtered).length > 0) {
            return JSON.stringify(filtered);
          }
          
          return JSON.stringify(value);
        }
        return String(value || '');
      };
      
      // Kiểm tra các nguồn attributes khác nhau
      let attributesToRender = [];
      
      // 1. Thử variant.attributes
      if (variant.attributes) {
        if (Array.isArray(variant.attributes)) {
          // Nếu là array
          attributesToRender = variant.attributes.map((attr, index) => (
            <span key={`attr-${index}`} style={{ marginRight: '8px' }}>
              {renderValue(attr)}
            </span>
          ));
        } else if (typeof variant.attributes === 'object') {
          // Nếu là object
          attributesToRender = Object.entries(variant.attributes).map(([key, value]) => (
            <span key={key} style={{ marginRight: '8px' }}>
              <strong>{key}:</strong> {renderValue(value)}
            </span>
          ));
        }
      }
      
      // 2. Thử variant.variant_attributes
      if (attributesToRender.length === 0 && variant.variant_attributes) {
        if (Array.isArray(variant.variant_attributes)) {
          attributesToRender = variant.variant_attributes.map((attr, index) => (
            <span key={`var-attr-${index}`} style={{ marginRight: '8px' }}>
              {renderValue(attr)}
            </span>
          ));
        } else if (typeof variant.variant_attributes === 'object') {
          attributesToRender = Object.entries(variant.variant_attributes).map(([key, value]) => (
            <span key={key} style={{ marginRight: '8px' }}>
              <strong>{key}:</strong> {renderValue(value)}
            </span>
          ));
        }
      }
      
      // 3. Thử variant.properties
      if (attributesToRender.length === 0 && variant.properties) {
        if (Array.isArray(variant.properties)) {
          attributesToRender = variant.properties.map((prop, index) => (
            <span key={`prop-${index}`} style={{ marginRight: '8px' }}>
              {renderValue(prop)}
            </span>
          ));
        } else if (typeof variant.properties === 'object') {
          attributesToRender = Object.entries(variant.properties).map(([key, value]) => (
            <span key={key} style={{ marginRight: '8px' }}>
              <strong>{key}:</strong> {renderValue(value)}
            </span>
          ));
        }
      }
      
      // 4. Thử variant.options
      if (attributesToRender.length === 0 && variant.options) {
        if (Array.isArray(variant.options)) {
          attributesToRender = variant.options.map((opt, index) => (
            <span key={`opt-${index}`} style={{ marginRight: '8px' }}>
              {renderValue(opt)}
            </span>
          ));
        } else if (typeof variant.options === 'object') {
          attributesToRender = Object.entries(variant.options).map(([key, value]) => (
            <span key={key} style={{ marginRight: '8px' }}>
              <strong>{key}:</strong> {renderValue(value)}
            </span>
          ));
        }
      }
      
      // Render kết quả
      if (attributesToRender.length > 0) {
        return (
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
            {attributesToRender}
          </div>
        );
      } else {
        return (
          <div style={{ fontSize: '12px', color: '#999', marginBottom: 2, fontStyle: 'italic' }}>
            {variant.name || variant.title || variant.description || 'Chưa có thuộc tính'}
          </div>
        );
      }
    } catch (error) {
     //console.error('❌ Error rendering variant attributes:', error, variant);
      return (
        <div style={{ fontSize: '12px', color: '#ff4d4f', marginBottom: 2, fontStyle: 'italic' }}>
          Lỗi hiển thị thuộc tính
        </div>
      );
    }
  };

  // Helper function để tự động chọn variant đầu tiên có tồn kho
  const autoSelectFirstVariant = (variants, showMessage = false) => {
    if (!variants || variants.length === 0) {
      return null;
    }
    
    // Tìm variant đầu tiên có tồn kho > 0
    const availableVariants = variants.filter(variant => 
      (variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) > 0
    );
    
    // Nếu có variant có tồn kho, chọn cái đầu tiên
    // Nếu không, chọn variant đầu tiên (kể cả hết hàng)
    const selectedVariant = availableVariants.length > 0 ? availableVariants[0] : variants[0];
    
   //console.log('🎯 Auto-selected variant:', {
    //   sku: selectedVariant?.sku,
    //   stock: selectedVariant?.stock || selectedVariant?.stock_quantity || selectedVariant?.quantity || selectedVariant?.available_quantity || 0,
    //   hasStock: (selectedVariant?.stock || selectedVariant?.stock_quantity || selectedVariant?.quantity || selectedVariant?.available_quantity || 0) > 0
    // });
    
    // Hiển thị thông báo ngắn nếu được yêu cầu
    if (showMessage && selectedVariant) {
      const stock = selectedVariant?.stock || selectedVariant?.stock_quantity || selectedVariant?.quantity || selectedVariant?.available_quantity || 0;
      if (stock > 0) {
        message.info(`Đã tự động chọn biến thể: ${selectedVariant.sku} (${stock} sản phẩm)`, 2);
      } else {
        message.warning(`Đã chọn biến thể: ${selectedVariant.sku} (Hết hàng)`, 2);
      }
    }
    
    return selectedVariant;
  };

  const handleProductClick = async (product) => {
    try {
      //console.log('🔍 Product clicked:', product.id, product.name);
      
      // Lấy chi tiết sản phẩm để có đầy đủ thông tin variants
      const detailResponse = await productService.getById(product.id);
      //console.log('📦 Product detail response:', detailResponse);
      
      if (detailResponse.success) {
        const productDetail = detailResponse.data;
        //console.log('🧬 Product variants:', productDetail.variants);
        //console.log('📊 Variants count:', productDetail.variants?.length || 0);
        
        setSelectedProduct(productDetail);
        
        if (productDetail.variants && productDetail.variants.length > 0) {
          //console.log('✅ Product has variants - opening modal');
          // Sản phẩm có variants - Mở modal chọn variant và tự động chọn variant đầu tiên
          
          const firstVariant = autoSelectFirstVariant(productDetail.variants, true);
          setSelectedVariant(firstVariant);
          setInputQuantity(1);
          setVariantModalVisible(true);
        } else {
          //console.log('❌ Product has no variants - adding directly');
          // Sản phẩm không có variants - Thêm trực tiếp
          handleAddToCart({
            variant_id: productDetail.id,
            product_name: productDetail.name,
            variant_sku: productDetail.sku || `PRODUCT-${productDetail.id}`,
            variant_price: productDetail.discount_price || productDetail.price,
            stock_quantity: productDetail.stock || 0,
            variant_image: productDetail.image_url,
            variant_attributes: null
          }, 1);
        }
      } else {
        //console.log('❌ Failed to get product detail or API error');
        message.error('Không thể tải thông tin chi tiết sản phẩm');
      }
    } catch (error) {
      //console.error('💥 Error loading product detail:', error);
      message.error('Không thể tải thông tin chi tiết sản phẩm: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAddToCart = (variantInfo, quantity) => {
    const existingItem = selectedItems.find(item => item.variant_id === variantInfo.variant_id);
    
    if (existingItem) {
      // Nếu đã có thì cộng thêm số lượng
      const newQuantity = existingItem.quantity + quantity;
      if (newQuantity <= variantInfo.stock_quantity) {
        handleUpdateQuantity(variantInfo.variant_id, newQuantity);
        message.success(`Đã thêm ${quantity} ${variantInfo.product_name}`);
      } else {
        message.warning(`Chỉ còn ${variantInfo.stock_quantity - existingItem.quantity} sản phẩm trong kho!`);
      }
    } else {
      // Nếu chưa có thì thêm mới
      if (variantInfo.stock_quantity >= quantity) {
        const newItem = {
          variant_id: variantInfo.variant_id,
          product_name: variantInfo.product_name,
          variant_sku: variantInfo.variant_sku,
          price: variantInfo.variant_price,
          quantity: quantity,
          stock_quantity: variantInfo.stock_quantity,
          variant_image: variantInfo.variant_image,
          variant_attributes: variantInfo.variant_attributes
        };
        setSelectedItems([...selectedItems, newItem]);
        message.success(`Đã thêm ${quantity} ${variantInfo.product_name}`);
      } else {
        message.warning('Không đủ tồn kho!');
      }
    }
  };

  const handleVariantModalOk = () => {
    if (!selectedVariant) {
      message.error('Vui lòng chọn biến thể');
      return;
    }
    
    if (inputQuantity <= 0) {
      message.error('Số lượng phải lớn hơn 0');
      return;
    }

    handleAddToCart({
      variant_id: selectedVariant.id,
      product_name: selectedProduct.name,
      variant_sku: selectedVariant.sku,
      variant_price: selectedVariant.discount_price || selectedVariant.price,
      stock_quantity: selectedVariant.stock || 0,
      variant_image: selectedProduct.image_url || selectedProduct.images?.[0]?.image_url,
      variant_attributes: selectedVariant.sku // Sử dụng SKU làm attributes hiển thị
    }, inputQuantity);

    setVariantModalVisible(false);
  };

  const handleVariantModalCancel = () => {
    setVariantModalVisible(false);
    setSelectedProduct(null);
    setSelectedVariant(null);
    setInputQuantity(1);
  };

  const handleUpdateQuantity = (variantId, quantity) => {
    if (quantity <= 0) {
      handleRemoveItem(variantId);
      return;
    }
    
    const newItems = selectedItems.map(item =>
      item.variant_id === variantId ? { ...item, quantity } : item
    );
    setSelectedItems(newItems);
  };

  const handleRemoveItem = (variantId) => {
    const newItems = selectedItems.filter(item => item.variant_id !== variantId);
    setSelectedItems(newItems);
  };

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      message.error('Vui lòng chọn ít nhất một sản phẩm');
      return;
    }

    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Lấy ID khách hàng đã chọn hoặc fallback admin ID
      let createdBy;
      if (selectedCustomer && selectedCustomer.id) {
        createdBy = selectedCustomer.id;
        //console.log('👤 Using selected customer ID as createdBy:', createdBy);
      } else {
        // Fallback: sử dụng ID admin/manager hiện tại nếu không chọn khách hàng
        try {
          const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
          createdBy = userInfo?.id || userInfo?.user_id || 1;
          //console.log('👤 Using admin/manager ID as createdBy (no customer selected):', createdBy);
        } catch (e) {
          //console.error('Error parsing userInfo:', e);
          createdBy = 1;
        }
      }

      // Xác định shipping_service dựa vào addressType
      let shippingService;
      if (addressType === 'pickup') {
        shippingService = 'nhận hàng tại quầy';
      } else {
        shippingService = selectedShippingOption ? selectedShippingOption.service_code : 'STK';
      }

      // Xây dựng invoiceData theo format API Management mới
      const invoiceData = {
        createdBy: createdBy,
        customerName: values.receiverName || selectedCustomer?.full_name || 'Khách lẻ',
        statusId: 1, // Chưa xử lý
        detailsJson: selectedItems.map(item => ({
          variant_id: item.variant_id,
          quantity: item.quantity,
          price: item.price
        })),
        shippingFee: shippingFee,
        discountAmount: discountAmount,
        note: values.note || '',
        paymentMethod: values.paymentMethod || paymentMethod || 'Tiền mặt',
        shippingService: shippingService
      };

      // Xử lý địa chỉ dựa vào addressType theo format API Management
      if (addressType === 'pickup') {
        // Nhận tại quầy - commonAddressId = 1
        invoiceData.commonAddressId = 1;
        invoiceData.receiverName = values.receiverName || selectedCustomer?.full_name || 'Khách lẻ';
        invoiceData.phone = values.phone || selectedCustomer?.SDT || '';
        invoiceData.province = 'Nhận tại quầy';
      } else if (addressType === 'new') {
        // Địa chỉ mới - không có commonAddressId, gửi chi tiết địa chỉ
        invoiceData.province = values.province;
        invoiceData.district = values.district;
        invoiceData.ward = values.ward;
        invoiceData.street = values.street;
        invoiceData.houseNumber = values.houseNumber;
        invoiceData.addressDetail = values.addressDetail;
        invoiceData.receiverName = values.receiverName;
        invoiceData.phone = values.phone;
      } else if (addressType && customerAddresses.length > 0) {
        // Địa chỉ có sẵn
        const selectedAddress = customerAddresses.find(addr => addr.id.toString() === addressType);
        if (selectedAddress) {
          invoiceData.commonAddressId = selectedAddress.id;
        } else {
          message.error('Không tìm thấy địa chỉ đã chọn');
          return;
        }
      } else {
        // Mặc định: khách lẻ, nhận tại quầy
        invoiceData.commonAddressId = 1;
        invoiceData.receiverName = values.receiverName || selectedCustomer?.full_name || 'Khách lẻ';
        invoiceData.phone = values.phone || selectedCustomer?.SDT || '';
        invoiceData.province = 'Nhận tại quầy';
        // Cập nhật shippingService cho trường hợp mặc định
        invoiceData.shippingService = 'nhận hàng tại quầy';
      }

      //console.log('📤 Sending invoice data (Management API):', invoiceData);
      //console.log('💰 Payment method:', paymentMethod);
      //console.log('🚚 Address type:', addressType);
      //console.log('🚚 Selected shipping option:', selectedShippingOption);
      //console.log('📋 Final shipping service:', shippingService);

      const response = await invoiceService.export.createForManagement(invoiceData);
      //console.log('📥 API response:', response);
      
      if (response.success) {
        const invoiceInfo = response.data;
        //console.log('📋 Invoice created successfully:', invoiceInfo);
        //console.log('📋 Full response data:', response.data);
        
        message.success(`Tạo hóa đơn thành công! Mã: ${invoiceInfo?.code}`);
        
        // Tạo QR thanh toán
        await generateQRPayment(invoiceInfo);
        
        // Reset form và state
        form.resetFields();
        setSelectedItems([]);
        setSelectedCustomer(null);
        setCustomerAddresses([]);
        setAddressType(null);
        setShippingFee(0);
        setDiscountAmount(0);
        setShippingOptions([]);
        setSelectedShippingOption(null);
        setPaymentMethod('Tiền mặt');
        
        // Có thể navigate hoặc refresh
        // navigate('/invoices/export');
      } else {
        message.error(response.message || 'Tạo hóa đơn thất bại');
      }
    } catch (error) {
      //console.error('💥 Error creating invoice:', error);
      message.error('Có lỗi xảy ra khi tạo hóa đơn: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const generateQRPayment = async (invoiceInfo) => {
    try {
      //console.log('🔄 Generating QR payment for invoice:', invoiceInfo);
      
      const soTien = parseInt(invoiceInfo.total_amount);
      const noiDungCk = `${invoiceInfo.code} - ${soTien}`;
      
      const qrResponse = await paymentService.generateQR({
        soTien: soTien,
        noiDungCk: noiDungCk
      });
      
      //console.log('📱 QR response:', qrResponse);
      
      if (qrResponse.success) {
        setQrData(qrResponse.data);
        // Thêm thông tin delivery type vào invoice info để sử dụng sau
        const enhancedInvoiceInfo = {
          ...invoiceInfo,
          current_delivery_type: addressType, // Lưu address type hiện tại
          current_selected_address_id: null // Sẽ được xác định dựa vào addressType
        };
        setCurrentInvoice(enhancedInvoiceInfo);
        setQrModalVisible(true);
        message.success('Tạo QR thanh toán thành công!');
      } else {
        message.error('Không thể tạo QR thanh toán: ' + (qrResponse.message || 'Lỗi không xác định'));
      }
    } catch (error) {
      //console.error('💥 Error generating QR:', error);
      
      // Kiểm tra nếu là lỗi 404 (API chưa có) thì skip QR
      if (error.response?.status === 404) {
        //console.log('⚠️ QR API not available, skipping QR generation');
        message.info('Chức năng QR thanh toán chưa khả dụng. Hóa đơn đã được tạo thành công!');
        return;
      }
      
      // Với các lỗi khác, vẫn hiển thị modal với thông tin cơ bản
      message.warning('Không thể tạo QR, hiển thị thông tin thanh toán cơ bản');
      
      // Tính toán lại các giá trị cần thiết cho mock data
      const soTien = parseInt(invoiceInfo.total_amount);
      const noiDungCk = `${invoiceInfo.code} - ${soTien}`;
      
      // Tạo mock data để hiển thị thông tin thanh toán
      const mockQRData = {
        payment_info: {
          so_tien: parseInt(invoiceInfo.total_amount),
          noi_dung_ck: noiDungCk,
          created_at: new Date().toISOString()
        },
        bank_info: {
          stk: "Chưa cấu hình",
          chu_tai_khoan: "Chưa cấu hình",
          bank_name: "Chưa cấu hình",
          bin: "000000"
        },
        qr_string: null // Không có QR
      };
      
      setQrData(mockQRData);
      // Thêm thông tin delivery type vào invoice info cho trường hợp lỗi
      const enhancedInvoiceInfo = {
        ...invoiceInfo,
        current_delivery_type: addressType,
        current_selected_address_id: null
      };
      setCurrentInvoice(enhancedInvoiceInfo);
      setQrModalVisible(true);
    }
  };

  // Xử lý xác nhận thanh toán
  const handleConfirmPayment = async () => {
    if (!currentInvoice) {
      message.error('Không tìm thấy thông tin hóa đơn');
      return;
    }
    
    try {
      //console.log('📋 Current invoice data:', currentInvoice);
      
      // Kiểm tra xem có phải nhận tại quầy không
      // Ưu tiên thông tin từ state hiện tại, sau đó mới kiểm tra dữ liệu từ API
      const isPickup = currentInvoice.current_delivery_type === 'pickup' ||
                      addressType === 'pickup' ||
                      currentInvoice.common_address_id === 1 || 
                      currentInvoice.pickup === 1 || 
                      currentInvoice.is_pickup === 1 ||
                      currentInvoice.delivery_type === 'pickup';
      
      //console.log('🚚 Delivery check:', {
      //   current_delivery_type: currentInvoice.current_delivery_type,
      //   currentStateAddressType: addressType,
      //   common_address_id: currentInvoice.common_address_id,
      //   pickup: currentInvoice.pickup,
      //   is_pickup: currentInvoice.is_pickup,
      //   delivery_type: currentInvoice.delivery_type,
      //   FINAL_isPickup: isPickup
      // });
      
      // Sử dụng trạng thái từ API:
      // - Nhận tại quầy: Trạng thái 6 "Đã Hoàn Thành" 
      // - Giao hàng: Trạng thái 2 "Đơn Đang Chuẩn Bị" (đã thanh toán)
      const statusId = isPickup ? 6 : 2; 
      
      // Tìm tên trạng thái từ danh sách API
      const statusInfo = invoiceStatuses.find(s => s.id === statusId);
      const statusName = statusInfo ? statusInfo.name : (isPickup ? 'Đã Hoàn Thành' : 'Đơn Đang Chuẩn Bị');
      const note = isPickup ? 'Đã hoàn thành đơn - Nhận tại quầy' : 'Đã thanh toán - Gửi đến kho';
      
      //console.log('📊 Status assignment:', {
      //   isPickup,
      //   statusId,
      //   statusName,
      //   note,
      //   foundStatusInfo: statusInfo
      // });
      
      // Kiểm tra ID hóa đơn
      const invoiceId = currentInvoice.id || currentInvoice.invoice_id || currentInvoice.export_id;
      
      if (!invoiceId) {
        //console.error('❌ Invoice ID not found in currentInvoice:', currentInvoice);
        message.error('Không tìm thấy ID hóa đơn để cập nhật trạng thái');
        return;
      }
      
      //console.log('🔄 Updating invoice status:', { 
      //   invoiceId, 
      //   statusId, 
      //   statusName,
      //   note,
      //   common_address_id: currentInvoice.common_address_id,
      //   isPickup,
      //   availableStatuses: invoiceStatuses,
      //   fullInvoiceData: currentInvoice
      // });
      
      const response = await invoiceService.export.updateStatus(invoiceId, {
        statusId: statusId,
        note: note
      });
      
      if (response.success) {
        message.success(`Cập nhật trạng thái "${statusName}" thành công!`);
        setQrModalVisible(false);
        setQrData(null);
        setCurrentInvoice(null);
        
        // Refresh trang hoặc load lại dữ liệu nếu cần
        window.location.reload(); // Có thể thay thế bằng callback để refresh data
      } else {
        message.error('Cập nhật trạng thái thất bại: ' + (response.message || 'Lỗi không xác định'));
      }
    } catch (error) {
      //console.error('💥 Error updating status:', error);
      message.error('Lỗi cập nhật trạng thái: ' + (error.response?.data?.message || error.message || 'Lỗi không xác định'));
    }
  };

  // Xử lý hủy thanh toán
  const handleCancelPayment = () => {
    setQrModalVisible(false);
    setQrData(null);
    setCurrentInvoice(null);
  };

  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    product.category_name?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  return (
    <>
      {/* Custom CSS cho scrollbar */}
      <style>
        {`
          .products-scroll-container {
            scrollbar-width: thin;
            scrollbar-color: #d9d9d9 #f5f5f5;
          }
          
          .products-scroll-container::-webkit-scrollbar {
            width: 8px;
          }
          
          .products-scroll-container::-webkit-scrollbar-track {
            background: #f5f5f5;
            border-radius: 4px;
          }
          
          .products-scroll-container::-webkit-scrollbar-thumb {
            background: #d9d9d9;
            border-radius: 4px;
          }
          
          .products-scroll-container::-webkit-scrollbar-thumb:hover {
            background: #bfbfbf;
          }
        `}
      </style>

      <div style={{ 
        padding: isMobile ? 8 : 16, 
        height: isMobile ? 'auto' : '100vh', 
        overflow: isMobile ? 'visible' : 'hidden', 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: isMobile ? '100vh' : 'auto'
      }}>
      <div style={{ 
        marginBottom: isMobile ? 12 : 16, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexShrink: 0,
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: isMobile ? 8 : 0
      }}>
        <Title level={isMobile ? 4 : 3} style={{ 
          margin: 0,
          fontSize: isMobile ? '18px' : '24px'
        }}>
          🛒 Tạo hóa đơn bán hàng
        </Title>
        <Button 
          onClick={() => navigate('/invoices/export')}
          size={isMobile ? 'small' : 'middle'}
          block={isMobile}
          style={isMobile ? { marginTop: 8, width: '100%' } : {}}
        >
          Quay lại
        </Button>
      </div>

      <Row gutter={isMobile ? [0, 16] : 16} style={{ 
        flex: 1, 
        minHeight: 0,
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'flex-start'
      }}>
        {/* TRÁI: Danh sách sản phẩm - Rộng và lớn */}
        <Col 
          xs={24} 
          lg={16} 
          style={{ 
            height: isMobile ? 'auto' : '100%', 
            display: 'flex', 
            flexDirection: 'column',
            marginBottom: isMobile ? 24 : 0,
            minHeight: isMobile ? '400px' : 'auto', // Tăng chiều cao tối thiểu cho mobile
            order: isMobile ? 1 : 1 // Danh sách sản phẩm luôn ở đầu trên mobile
          }}
        >
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: isMobile ? 14 : 16 }}>
                    <ShoppingCartOutlined /> Danh mục sản phẩm ({filteredProducts.length})
                  </span>
                  {isMobile && (
                    <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                      📱 Bước 1: Chọn sản phẩm trước
                    </span>
                  )}
                </div>
                <Button 
                  size="small"
                  onClick={loadProducts}
                  loading={productsLoading}
                  style={{ fontSize: '12px' }}
                  type={products.length === 0 && !productsLoading ? 'primary' : 'default'}
                  danger={products.length === 0 && !productsLoading}
                >
                  {products.length === 0 && !productsLoading ? '⚠️ Thử lại' : '🔄 Refresh'}
                </Button>
              </div>
            }
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            bodyStyle={{ flex: 1, padding: isMobile ? '12px' : '16px', display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            {/* Tìm kiếm sản phẩm */}
            <Input
              placeholder="🔍 Tìm kiếm sản phẩm..."
              prefix={<SearchOutlined />}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              style={{ marginBottom: 16, flexShrink: 0 }}
              size={isMobile ? "middle" : "large"}
            />
            
            {/* Container scroll cho sản phẩm */}
            <div 
              className="products-scroll-container"
              style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: 0,
                borderRadius: '4px',
                maxHeight: isMobile ? '600px' : 'none', // Tăng chiều cao tối đa trên mobile
                WebkitOverflowScrolling: 'touch', // Smooth scrolling trên iOS
                border: isMobile ? '1px solid #f0f0f0' : 'none' // Thêm border trên mobile để rõ ràng hơn
              }}
            >
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 
                  'repeat(auto-fill, minmax(150px, 1fr))' : // Mobile: 150px minimum
                  'repeat(auto-fill, minmax(200px, 1fr))', // Desktop: 200px minimum
                gap: isMobile ? '8px' : '12px',
                padding: isMobile ? '8px' : '12px',
                minHeight: '100%'
              }}>
                {productsLoading ? (
                  <div style={{ 
                    gridColumn: '1 / -1', 
                    textAlign: 'center', 
                    padding: '60px'
                  }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
                    <div style={{ marginTop: 16, color: '#666' }}>Đang tải sản phẩm...</div>
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div style={{ 
                    gridColumn: '1 / -1', 
                    textAlign: 'center', 
                    color: '#999',
                    padding: '40px',
                    fontSize: '16px'
                  }}>
                    {searchKeyword ? 
                      `🔍 Không tìm thấy sản phẩm nào với từ khóa "${searchKeyword}"` : 
                      <>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                        <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px', color: '#666' }}>
                          Chưa có sản phẩm nào
                        </div>
                        <div style={{ fontSize: '14px', color: '#999', marginBottom: '16px' }}>
                          Hệ thống chưa có sản phẩm hoặc không thể kết nối đến API
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          💡 Hướng dẫn khắc phục:
                          <br />• Kiểm tra kết nối internet
                          <br />• Đảm bảo đã đăng nhập với quyền phù hợp
                          <br />• Nhấn nút "⚠️ Thử lại" để tải lại danh sách
                          <br />• Liên hệ admin nếu vấn đề vẫn tiếp diễn
                        </div>
                      </>
                    }
                  </div>
                ) : (
                  filteredProducts.map(product => (
                    <Card
                      key={product.id}
                      hoverable
                      style={{ 
                        cursor: 'pointer',
                        borderRadius: '12px', // Bo góc đẹp hơn
                        height: '300px', // Giảm từ 340px xuống 300px để giảm khoảng trắng
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        border: '1px solid #f0f0f0',
                        overflow: 'hidden' // Đảm bảo không bị tràn
                      }}
                      bodyStyle={{ 
                        padding: '10px', // Giảm từ 12px xuống 10px để tiết kiệm không gian
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      onClick={() => handleProductClick(product)}
                    >
                      <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <img 
                          src={product.image_url || '/placeholder.png'} 
                          alt={product.name}
                          style={{ 
                            width: '100%', 
                            height: '140px', // Tăng từ 120px lên 140px để cân bằng tỷ lệ
                            objectFit: 'cover', 
                            objectPosition: 'center', // Căn giữa ảnh
                            borderRadius: '8px', // Bo góc đẹp hơn
                            marginBottom: '8px',
                            border: '1px solid #f0f0f0', // Viền nhẹ
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', // Đổ bóng nhẹ
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease' // Hiệu ứng hover
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.02)';
                            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                          }}
                        />
                        <div style={{ 
                          fontWeight: 'bold', 
                          fontSize: '13px', // Giảm từ 15px xuống 13px để phù hợp với card nhỏ hơn
                          marginBottom: '4px', // Giảm margin
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          flex: '0 0 auto',
                          minHeight: '36px', // Giảm minHeight để tiết kiệm không gian
                          lineHeight: '1.3' // Giảm line height
                        }}>
                          {product.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                          {product.category_name}
                        </div>
                        <div style={{ 
                          fontSize: '14px', // Giảm từ 16px xuống 14px 
                          fontWeight: 'bold', 
                          color: '#1890ff',
                          marginBottom: '6px', // Giảm margin
                          flex: '0 0 auto'
                        }}>
                          {product.discount_price ? (
                            <>
                              <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '12px' }}>
                                {formatCurrency(product.price)}
                              </span>
                              <br />
                              {formatCurrency(product.discount_price)}
                            </>
                          ) : (
                            formatCurrency(product.price)
                          )}
                        </div>
                        
                        {/* Rating nếu có */}
                        {product.avg_rating && (
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                            ⭐ {parseFloat(product.avg_rating).toFixed(1)} ({product.review_count} đánh giá)
                          </div>
                        )}
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </Card>
        </Col>

        {/* PHẢI: Thông tin đơn hàng */}
        <Col xs={24} lg={8} style={{ 
          height: isMobile ? 'auto' : '100%',
          order: isMobile ? 2 : 2 // Form khách hàng ở dưới trên mobile (sau khi chọn sản phẩm)
        }}>
          <div style={{ 
            height: isMobile ? 'auto' : 'calc(100vh - 120px)', 
            overflow: isMobile ? 'visible' : 'auto',
            paddingRight: isMobile ? '0' : '8px',
            marginBottom: isMobile ? '16px' : '0'
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: isMobile ? '12px' : '16px', 
              minHeight: isMobile ? 'auto' : '100%' 
            }}>
              {/* Form khách hàng */}
              <Card 
                title={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span><UserOutlined /> Khách hàng</span>
                    {isMobile && selectedItems.length === 0 && (
                      <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                        📝 Bước 2: Nhập thông tin khách hàng
                      </span>
                    )}
                  </div>
                } 
                size="small"
                style={{ flexShrink: 0 }}
              >
              <Form
                form={form}
                layout="vertical"
                size="small"
              >
                <Form.Item name="customerId" label="Chọn khách hàng">
                  <Select
                    showSearch
                    placeholder="Nhập tên hoặc SĐT để tìm khách hàng..."
                    filterOption={false}
                    onSearch={loadCustomers}
                    onChange={handleCustomerChange}
                    allowClear
                    size="small"
                    notFoundContent={customers.length === 0 ? "Nhập ít nhất 2 ký tự để tìm kiếm" : "Không tìm thấy khách hàng"}
                  >
                    {customers.map(customer => (
                      <Option key={customer.id} value={customer.id}>
                        {customer.full_name} {customer.SDT ? `- ${customer.SDT}` : ''} ({customer.username})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="addressType" label="Hình thức giao hàng">
                  <Select
                    placeholder="Chọn hình thức giao hàng"
                    onChange={handleAddressTypeChange}
                    size="small"
                  >
                    <Option value="pickup">🏪 Nhận hàng tại quầy</Option>
                    {selectedCustomer && customerAddresses.map(address => (
                      <Option key={address.id} value={address.id.toString()}>
                        📍 {address.name}: {address.address}
                        {address.is_default && <span style={{ color: '#1890ff' }}> (Mặc định)</span>}
                      </Option>
                    ))}
                    <Option value="new">➕ Thêm địa chỉ mới</Option>
                  </Select>
                </Form.Item>

                <Form.Item name="paymentMethod" label="Hình thức thanh toán" initialValue="Tiền mặt">
                  <Select
                    placeholder="Chọn hình thức thanh toán"
                    onChange={(value) => {
                      setPaymentMethod(value);
                      form.setFieldsValue({ paymentMethod: value });
                    }}
                    size="small"
                  >
                    <Option value="Tiền mặt">💵 Tiền mặt</Option>
                    <Option value="Chuyển khoản">🏦 Chuyển khoản</Option>
                    <Option value="COD">📦 Thu hộ (COD)</Option>
                  </Select>
                </Form.Item>
                
                <Row gutter={isMobile ? 4 : 8}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="receiverName" label="Người nhận">
                      <Input placeholder="Tên người nhận" size="small" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="phone" label="SĐT">
                      <Input placeholder="Số điện thoại" size="small" />
                    </Form.Item>
                  </Col>
                </Row>
                
                {/* Hiển thị form địa chỉ chi tiết khi chọn "Thêm địa chỉ mới" */}
                {addressType === 'new' ? (
                  <>
                    <Row gutter={isMobile ? 4 : 8}>
                      <Col xs={24} sm={8}>
                        <Form.Item name="province" label="Tỉnh/Thành phố" rules={[{ required: true, message: 'Vui lòng chọn tỉnh/thành phố' }]}>
                          <Select 
                            placeholder="Chọn tỉnh/thành phố" 
                            size="small" 
                            showSearch
                            loading={locationLoading.provinces}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            onChange={(provinceId) => {
                              // Clear dependent fields
                              form.setFieldsValue({ district: null, ward: null });
                              // Load districts for selected province
                              if (provinceId) {
                                loadDistricts(provinceId);
                              } else {
                                setDistricts([]);
                                setWards([]);
                              }
                            }}
                          >
                            {provinces.map(province => (
                              <Option key={province.PROVINCE_ID} value={province.PROVINCE_ID}>
                                {province.PROVINCE_NAME}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name="district" label="Quận/Huyện" rules={[{ required: true, message: 'Vui lòng chọn quận/huyện' }]}>
                          <Select 
                            placeholder="Chọn quận/huyện" 
                            size="small"
                            showSearch
                            loading={locationLoading.districts}
                            disabled={districts.length === 0}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            onChange={(districtId) => {
                              // Clear dependent field
                              form.setFieldsValue({ ward: null });
                              // Load wards for selected district
                              if (districtId) {
                                loadWards(districtId);
                              } else {
                                setWards([]);
                              }
                            }}
                          >
                            {districts.map(district => (
                              <Option key={district.DISTRICT_ID} value={district.DISTRICT_ID}>
                                {district.DISTRICT_NAME}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item name="ward" label="Phường/Xã" rules={[{ required: true, message: 'Vui lòng chọn phường/xã' }]}>
                          <Select 
                            placeholder="Chọn phường/xã"
                            size="small"
                            showSearch
                            loading={locationLoading.wards}
                            disabled={wards.length === 0}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                          >
                            {wards.map(ward => (
                              <Option key={ward.WARDS_ID} value={ward.WARDS_ID}>
                                {ward.WARDS_NAME}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row gutter={8}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="street" label="Đường/Phố">
                          <Input placeholder="Tên đường/phố" size="small" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="houseNumber" label="Số nhà">
                          <Input placeholder="Số nhà" size="small" />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Form.Item name="addressDetail" label="Chi tiết địa chỉ">
                      <Input.TextArea 
                        placeholder="Tòa nhà, tầng, căn hộ, ghi chú thêm..." 
                        rows={2} 
                        size="small"
                      />
                    </Form.Item>
                    
                    <Form.Item name="note" label="Ghi chú giao hàng">
                      <Input.TextArea 
                        placeholder="Ghi chú đặc biệt cho người giao hàng..." 
                        rows={2} 
                        size="small"
                      />
                    </Form.Item>
                  </>
                ) : (
                  <Form.Item name="address" label="Địa chỉ">
                    <Input.TextArea 
                      placeholder="Địa chỉ giao hàng..." 
                      rows={2} 
                      size="small"
                      disabled={addressType === 'pickup'}
                    />
                  </Form.Item>
                )}

                {/* Section tính phí ship ViettelPost */}
                {(addressType === 'new' || (addressType && customerAddresses.length > 0 && addressType !== 'pickup')) && (
                  <Card 
                    title="🚚 Tính phí vận chuyển ViettelPost"
                    size="small"
                    style={{ marginTop: 16 }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ marginBottom: 12 }}>
                      <Button 
                        type="primary"
                        icon={<SearchOutlined />}
                        loading={shippingLoading}
                        onClick={handleCalculateShipping}
                        size="small"
                        style={{ width: '100%' }}
                        disabled={
                          addressType === 'new' 
                            ? (!form.getFieldValue('province') && !form.getFieldValue('district') && !form.getFieldValue('ward'))
                            : false
                        }
                      >
                        Tính phí vận chuyển
                      </Button>
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', textAlign: 'center' }}>
                        {addressType === 'new' 
                          ? 'Vui lòng điền ít nhất một thông tin địa chỉ để tính phí'
                          : 'Tính phí vận chuyển đến địa chỉ đã chọn'
                        }
                      </div>
                    </div>

                    {shippingOptions.length > 0 && (
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '8px' }}>
                          Chọn dịch vụ vận chuyển ({shippingOptions.length} options):
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px' }}>
                          <Radio.Group
                            value={selectedShippingOption?.id}
                            onChange={(e) => {
                              const option = shippingOptions.find(opt => opt.id === e.target.value);
                              handleShippingOptionSelect(option);
                            }}
                            style={{ width: '100%' }}
                          >
                            {shippingOptions.map((option, index) => (
                              <div key={option.id} style={{ 
                                marginBottom: '8px',
                                padding: '8px',
                                border: '1px solid #f0f0f0',
                                borderRadius: '4px',
                                backgroundColor: selectedShippingOption?.id === option.id ? '#e6f7ff' : '#fafafa'
                              }}>
                                <Radio value={option.id} style={{ fontSize: '12px', width: '100%' }}>
                                  <div style={{ width: '100%' }}>
                                    <div style={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between', 
                                      alignItems: 'center',
                                      marginBottom: '4px'
                                    }}>
                                      <span style={{ fontWeight: '500', fontSize: '13px' }}>
                                        {option.name}
                                      </span>
                                      <span style={{ 
                                        fontWeight: 'bold', 
                                        color: index === 0 ? '#52c41a' : index === shippingOptions.length - 1 ? '#fa8c16' : '#1890ff',
                                        fontSize: '13px'
                                      }}>
                                        {formatCurrency(option.price)}
                                      </span>
                                    </div>
                                    <div style={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between',
                                      fontSize: '11px', 
                                      color: '#666'
                                    }}>
                                      <span>⏱️ {option.delivery_time}</span>
                                      <span>📋 {option.service_code}</span>
                                    </div>
                                    {index === 0 && (
                                      <div style={{ fontSize: '10px', color: '#52c41a', marginTop: '2px' }}>
                                        💰 Rẻ nhất
                                      </div>
                                    )}
                                    {option.delivery_time === '24 giờ' && (
                                      <div style={{ fontSize: '10px', color: '#fa8c16', marginTop: '2px' }}>
                                        ⚡ Nhanh nhất
                                      </div>
                                    )}
                                  </div>
                                </Radio>
                              </div>
                            ))}
                          </Radio.Group>
                        </div>
                        
                        {selectedShippingOption && (
                          <div style={{ 
                            marginTop: '8px', 
                            padding: '8px', 
                            backgroundColor: '#f0f9ff', 
                            border: '1px solid #bae6fd',
                            borderRadius: '4px'
                          }}>
                            <div style={{ fontSize: '12px', fontWeight: '500', color: '#0369a1' }}>
                              ✅ Đã chọn: {selectedShippingOption.name}
                            </div>
                            <div style={{ fontSize: '11px', color: '#0369a1' }}>
                              💰 Phí: {formatCurrency(selectedShippingOption.price)} | ⏱️ {selectedShippingOption.delivery_time} | 📋 {selectedShippingOption.service_code}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {shippingOptions.length === 0 && !shippingLoading && (
                      <div style={{ 
                        textAlign: 'center', 
                        color: '#666', 
                        fontSize: '11px',
                        padding: '8px'
                      }}>
                        Chưa có dữ liệu phí ship. Nhấn "Tính phí vận chuyển" để lấy báo giá.
                      </div>
                    )}
                  </Card>
                )}
              </Form>
            </Card>

            {/* Giỏ hàng */}
            <Card 
              title={
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? '8px' : '0' }}>
                    <span style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '500' }}>🛍️ Giỏ hàng ({selectedItems.length})</span>
                    <Button 
                      type="primary" 
                      size={isMobile ? "middle" : "small"}
                      icon={<SaveOutlined />}
                      loading={loading}
                      disabled={selectedItems.length === 0}
                      onClick={handleSubmit}
                      style={{ 
                        fontSize: isMobile ? '14px' : '12px',
                        height: isMobile ? '36px' : 'auto',
                        minWidth: isMobile ? '120px' : 'auto'
                      }}
                    >
                      💰 Thanh toán
                    </Button>
                  </div>
                  
                  {/* Bảng tính toán */}
                  <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span>Tiền hàng:</span>
                      <span style={{ fontWeight: '500' }}>{formatCurrency(subtotal)}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span>Phí ship:</span>
                      <InputNumber
                        min={0}
                        value={shippingFee}
                        onChange={setShippingFee}
                        size="small"
                        style={{ width: '100px' }}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span>Giảm giá:</span>
                      <InputNumber
                        min={0}
                        max={subtotal}
                        value={discountAmount}
                        onChange={setDiscountAmount}
                        size="small"
                        style={{ width: '100px' }}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      />
                    </div>

                    {/* Hiển thị thông tin thanh toán và vận chuyển */}
                    {(form.getFieldValue('paymentMethod') || paymentMethod) && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span>Thanh toán:</span>
                        <span style={{ fontSize: '12px', color: '#1890ff' }}>{form.getFieldValue('paymentMethod') || paymentMethod}</span>
                      </div>
                    )}
                    
                    {selectedShippingOption && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span>Vận chuyển:</span>
                        <span style={{ fontSize: '12px', color: '#52c41a' }}>
                          {selectedShippingOption.service_code} - {selectedShippingOption.name}
                        </span>
                      </div>
                    )}
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      borderTop: '1px solid #d9d9d9',
                      paddingTop: '6px',
                      fontSize: '15px',
                      fontWeight: 'bold',
                      color: '#1890ff'
                    }}>
                      <span>TỔNG CỘNG:</span>
                      <span>{formatCurrency(total)}</span>
                    </div>
                  </div>
                </div>
              }
              style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column'
              }}
              bodyStyle={{ 
                flex: 1, 
                overflow: 'visible', 
                padding: '8px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ flex: 1, marginBottom: '16px' }}>
                {selectedItems.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    color: '#999', 
                    padding: '40px 0',
                    fontSize: '14px'
                  }}>
                    🛒 Chưa có sản phẩm nào
                    <br />
                    <small>Nhấp vào sản phẩm để thêm</small>
                  </div>
                ) : (
                  selectedItems.map(item => (
                    <div 
                      key={item.variant_id}
                      style={{ 
                        border: '1px solid #f0f0f0',
                        borderRadius: '6px',
                        padding: '8px',
                        marginBottom: '8px',
                        backgroundColor: '#fafafa'
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: 'bold', 
                            fontSize: '13px',
                            marginBottom: '2px'
                          }}>
                            {item.product_name}
                          </div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            {formatCurrency(item.price)}
                          </div>
                        </div>
                        <Button 
                          type="text" 
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveItem(item.variant_id)}
                          style={{ padding: '4px' }}
                        />
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <InputNumber
                          min={1}
                          max={item.stock_quantity}
                          value={item.quantity}
                          onChange={(value) => handleUpdateQuantity(item.variant_id, value)}
                          size="small"
                          style={{ width: '60px' }}
                        />
                        <span style={{ fontSize: '12px', color: '#666' }}>×</span>
                        <div style={{ 
                          fontWeight: 'bold', 
                          color: '#1890ff',
                          fontSize: '13px'
                        }}>
                          {formatCurrency(item.quantity * item.price)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
            </div>
          </div>
        </Col>
      </Row>

      {/* Modal chọn variant */}
      <Modal
        title={`Chọn biến thể - ${selectedProduct?.name || ''}`}
        visible={variantModalVisible}
        onCancel={handleVariantModalCancel}
        width={isMobile ? '95%' : 800}
        style={isMobile ? { top: 20 } : {}}
        footer={null}
      >
        {selectedProduct && (
          <div>
            {/* Layout chính: Ảnh bên trái, chức năng bên phải */}
            <Row gutter={24} style={{ marginBottom: 20 }}>
              <Col xs={24} sm={8}>
                {/* Ảnh sản phẩm */}
                <img 
                  src={selectedProduct.image_url || selectedProduct.images?.[0]?.image_url || '/placeholder.png'}
                  alt={selectedProduct.name}
                  style={{ 
                    width: '100%', 
                    height: isMobile ? '200px' : '240px', // Điều chỉnh chiều cao cho mobile
                    objectFit: 'cover',
                    objectPosition: 'center',
                    borderRadius: '12px', // Bo góc lớn hơn cho modal
                    border: '2px solid #f0f0f0',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)' // Đổ bóng đẹp hơn cho modal
                  }}
                />
              </Col>
              <Col xs={24} sm={16}>
                {/* Phần chọn biến thể và chức năng */}
                <div style={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  marginTop: isMobile ? 16 : 0 // Thêm margin top trên mobile
                }}>
                  <h4 style={{ marginBottom: 16 }}>
                    Chọn biến thể: 
                    {selectedVariant && (
                      <span style={{ fontSize: '12px', color: '#52c41a', fontWeight: 'normal', marginLeft: '8px' }}>
                        ✅ Đã chọn: {selectedVariant.sku}
                      </span>
                    )}
                  </h4>
                  
                  <Radio.Group 
                    value={selectedVariant?.id} 
                    onChange={(e) => {
                      const variant = selectedProduct.variants.find(v => v.id === e.target.value);
                      setSelectedVariant(variant);
                      setInputQuantity(1);
                    }}
                    style={{ width: '100%', marginBottom: 16, flex: 1 }}
                  >
                    <div style={{ maxHeight: '120px', overflowY: 'auto', paddingRight: '8px' }}>
                      {selectedProduct.variants?.map(variant => (
                        <div key={variant.id} style={{ 
                          marginBottom: 8,
                          opacity: (variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) === 0 ? 0.6 : 1,
                          backgroundColor: (variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) === 0 ? '#f5f5f5' : 'transparent',
                          borderRadius: '4px',
                          padding: '4px'
                        }}>
                          <Radio 
                            value={variant.id} 
                            style={{ width: '100%' }}
                            disabled={(variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) === 0}
                          >
                            <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                              <Col flex="1">
                                <div>
                                  <div style={{ fontWeight: 'bold', marginBottom: 2 }}>
                                    {variant.sku}
                                  </div>
                                  
                                  {/* Hiển thị thuộc tính từ nhiều nguồn có thể - Sử dụng helper function an toàn */}
                                  {renderVariantAttributes(variant)}
                                  <div style={{ 
                                    fontSize: '12px', 
                                    color: (variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) > 10 ? '#52c41a' : (variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) > 0 ? '#fa8c16' : '#ff4d4f',
                                    fontWeight: '500'
                                  }}>
                                    📦 Kho: {variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0}
                                    {(variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) <= 5 && (variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) > 0 && (
                                      <span style={{ color: '#fa8c16', fontSize: '11px', marginLeft: '5px' }}>
                                        (Sắp hết)
                                      </span>
                                    )}
                                    {(variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) === 0 && (
                                      <span style={{ color: '#ff4d4f', fontSize: '11px', marginLeft: '5px' }}>
                                        (Hết hàng)
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </Col>
                              <Col>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ 
                                    fontWeight: 'bold', 
                                    color: (variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) === 0 ? '#999' : '#1890ff'
                                  }}>
                                    {formatCurrency(variant.price)}
                                    {(variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) === 0 && (
                                      <div style={{ fontSize: '10px', color: '#ff4d4f' }}>
                                        (Hết hàng)
                                      </div>
                                    )}
                                  </div>
                                  {variant.discount_price && variant.discount_price < variant.price && (variant.stock || variant.stock_quantity || variant.quantity || variant.available_quantity || 0) > 0 && (
                                    <div style={{ fontSize: '11px', color: '#999', textDecoration: 'line-through' }}>
                                      {formatCurrency(variant.price)}
                                    </div>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          </Radio>
                        </div>
                      ))}
                    </div>
                  </Radio.Group>

                  {/* Phần số lượng, thành tiền và các nút */}
                  {selectedVariant && (
                    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                      <Row gutter={12} align="middle">
                        <Col xs={24} sm={8}>
                          <div style={{ marginBottom: 4, fontWeight: 500 }}>Số lượng:</div>
                          <InputNumber
                            min={1}
                            max={selectedVariant.stock || selectedVariant.stock_quantity || selectedVariant.quantity || selectedVariant.available_quantity || 0}
                            value={inputQuantity}
                            onChange={(value) => {
                              const maxStock = selectedVariant.stock || selectedVariant.stock_quantity || selectedVariant.quantity || selectedVariant.available_quantity || 0;
                              if (value === null || value === undefined) {
                                setInputQuantity(1);
                              } else if (value < 1) {
                                setInputQuantity(1);
                              } else if (value > maxStock) {
                                setInputQuantity(maxStock);
                                message.warning(`Chỉ còn ${maxStock} sản phẩm trong kho!`);
                              } else {
                                setInputQuantity(value);
                              }
                            }}
                            onBlur={(e) => {
                              const value = parseInt(e.target.value);
                              const maxStock = selectedVariant.stock || selectedVariant.stock_quantity || selectedVariant.quantity || selectedVariant.available_quantity || 0;
                              if (value > maxStock) {
                                setInputQuantity(maxStock);
                                message.warning(`Đã tự động điều chỉnh về ${maxStock} (số lượng tối đa)`);
                              }
                            }}
                            style={{ width: '100%' }}
                            placeholder={`Tối đa: ${selectedVariant.stock || selectedVariant.stock_quantity || selectedVariant.quantity || selectedVariant.available_quantity || 0}`}
                            precision={0}
                            step={1}
                          />
                          {selectedVariant && (
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                              Còn lại: {selectedVariant.stock || selectedVariant.stock_quantity || selectedVariant.quantity || selectedVariant.available_quantity || 0} sản phẩm
                            </div>
                          )}
                        </Col>
                        <Col xs={24} sm={8}>
                          <div style={{ marginBottom: 4, fontWeight: 500 }}>Thành tiền:</div>
                          <Input
                            value={formatCurrency((selectedVariant?.price || 0) * inputQuantity)}
                            readOnly
                            style={{ 
                              fontWeight: 'bold', 
                              color: '#d32f2f',
                              backgroundColor: '#fafafa'
                            }}
                          />
                        </Col>
                        <Col xs={24} sm={8}>
                          <div style={{ marginBottom: 4, fontWeight: 500 }}>Hành động:</div>
                          <Space>
                            <Button onClick={handleVariantModalCancel} size="small">
                              Hủy
                            </Button>
                            <Button 
                              type="primary" 
                              onClick={handleVariantModalOk}
                              size="small"
                              disabled={
                                !selectedVariant || 
                                inputQuantity <= 0 || 
                                inputQuantity > (selectedVariant.stock || selectedVariant.stock_quantity || selectedVariant.quantity || selectedVariant.available_quantity || 0)
                              }
                            >
                              Thêm giỏ
                            </Button>
                          </Space>
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
            
            {/* Thông tin sản phẩm ở cuối */}
            <Divider />
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '16px', 
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <h4 style={{ marginBottom: 12, color: '#495057' }}>Thông tin sản phẩm:</h4>
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={12}>
                  <strong>Tên sản phẩm:</strong> {selectedProduct.name}
                </Col>
                <Col xs={24} sm={12}>
                  <strong>Danh mục:</strong> {selectedProduct.category_name}
                </Col>
                {selectedProduct.avg_rating && (
                  <Col xs={24} sm={12}>
                    <strong>Đánh giá:</strong> ⭐ {parseFloat(selectedProduct.avg_rating).toFixed(1)} ({selectedProduct.review_count} reviews)
                  </Col>
                )}
                <Col xs={24} sm={12}>
                  <strong>Biến thể:</strong> {selectedProduct.variants?.length || 0} loại
                </Col>
                {selectedProduct.description && (
                  <Col xs={24}>
                    <div>
                      <strong>Mô tả:</strong>
                      <div style={{ 
                        marginTop: '4px', 
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.4',
                        color: '#666'
                      }}>
                        {selectedProduct.description}
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal QR thanh toán */}
      <Modal
        title="QR Thanh toán"
        visible={qrModalVisible}
        onCancel={handleCancelPayment}
        width={isMobile ? '95%' : 500}
        style={isMobile ? { top: 20 } : {}}
        footer={[
          <Button key="cancel" onClick={handleCancelPayment}>
            Hủy
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            onClick={handleConfirmPayment}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            Xác nhận thanh toán
          </Button>
        ]}
      >
        {qrData && currentInvoice && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#1890ff', marginBottom: '8px' }}>
                Hóa đơn: {currentInvoice.code}
              </h3>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f5222d' }}>
                {formatCurrency(parseInt(currentInvoice.total_amount))}
              </div>
            </div>
            
            {/* QR Code hoặc thông báo */}
            {qrData.qr_string ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginBottom: '20px',
                padding: '20px',
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', marginBottom: '10px', color: '#666' }}>
                    Quét mã QR để thanh toán
                  </div>
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData.qr_string)}`}
                    alt="QR Code"
                    style={{ width: '200px', height: '200px' }}
                  />
                </div>
              </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginBottom: '20px',
                padding: '40px 20px',
                border: '2px dashed #faad14',
                borderRadius: '8px',
                backgroundColor: '#fffbe6',
                textAlign: 'center'
              }}>
                <div>
                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>🏦</div>
                  <div style={{ fontSize: '14px', color: '#d48806', fontWeight: '500' }}>
                    Chức năng QR chưa khả dụng
                  </div>
                  <div style={{ fontSize: '12px', color: '#ad6800', marginTop: '4px' }}>
                    Vui lòng chuyển khoản theo thông tin bên dưới
                  </div>
                </div>
              </div>
            )}
            
            {/* Thông tin ngân hàng */}
            <div style={{ 
              backgroundColor: '#f6ffed', 
              border: '1px solid #b7eb8f',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#389e0d' }}>
                Thông tin chuyển khoản
              </div>
              <div><strong>Ngân hàng:</strong> {qrData.bank_info.bank_name}</div>
              <div><strong>Số TK:</strong> {qrData.bank_info.stk}</div>
              <div><strong>Chủ TK:</strong> {qrData.bank_info.chu_tai_khoan}</div>
              <div><strong>Số tiền:</strong> {formatCurrency(qrData.payment_info.so_tien)}</div>
              <div><strong>Nội dung:</strong> {qrData.payment_info.noi_dung_ck}</div>
            </div>
            
            <div style={{ color: '#666', fontSize: '12px' }}>
              Vui lòng chuyển khoản đúng số tiền và nội dung để đơn hàng được xử lý nhanh nhất
            </div>
          </div>
        )}
      </Modal>
    </div>
    </>
  );
};

export default InvoiceExportForm;
